// FetchResumesModal — shows candidates from the Talent Pool scored against
// the current job's JD. Only candidates NOT already on this job are shown,
// ranked by keyword/skill overlap with the job description.

import React, { useEffect, useState } from 'react';
import { X, Search, Loader2, CheckCircle, FileText, Star, Sparkles } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../../services/api';

const MATCH_COLOR = score =>
  score >= 75 ? 'text-emerald-600 bg-emerald-50' :
  score >= 50 ? 'text-orange-500 bg-orange-50' :
               'text-gray-500 bg-gray-100';

const POTENTIAL_LABEL = {
  high_potential:   { label: 'High Potential',  cls: 'bg-emerald-100 text-emerald-700' },
  future_fit:       { label: 'Future Fit',      cls: 'bg-indigo-100 text-indigo-700'   },
  silver_medalist:  { label: 'Silver Medalist', cls: 'bg-slate-100 text-slate-600'     },
  reconsider_later: { label: 'Reconsider',      cls: 'bg-amber-100 text-amber-700'     },
};

const FetchResumesModal = ({ isOpen, onClose, jobId, onResumesSelected }) => {
  const [q, setQ]             = useState('');
  const [items, setItems]     = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked]   = useState(() => new Set());
  const [attaching, setAttaching] = useState(false);
  const [minScore, setMinScore] = useState(0);

  useEffect(() => {
    if (!isOpen || !jobId) return;
    setLoading(true);
    api.get(`/talent-pool/match/${jobId}`)
      .then(r => { setItems(r.data?.data || []); })
      .catch(() => toast.error('Failed to load talent pool candidates'))
      .finally(() => setLoading(false));
  }, [isOpen, jobId]);

  // Client-side search + score filter
  useEffect(() => {
    const lq = q.toLowerCase();
    setFiltered(
      items.filter(c =>
        c.jdMatchScore >= minScore &&
        (!lq || c.candidateName?.toLowerCase().includes(lq) || c.email?.toLowerCase().includes(lq) || c.appliedJD?.toLowerCase().includes(lq))
      )
    );
  }, [items, q, minScore]);

  useEffect(() => { if (!isOpen) { setPicked(new Set()); setQ(''); setMinScore(0); } }, [isOpen]);

  if (!isOpen) return null;

  const togglePick = (id) => {
    setPicked(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => setPicked(new Set(filtered.map(r => String(r._id))));
  const clearAll  = () => setPicked(new Set());

  const handleAttach = async () => {
    const resumeIds = Array.from(picked);
    if (!resumeIds.length) return;
    if (!jobId) return toast.error('Job not loaded yet');
    try {
      setAttaching(true);
      const r = await api.post('/resumes/bank/attach', { jobId, resumeIds });
      const count = r.data?.count || resumeIds.length;
      toast.success(`Added ${count} candidate${count === 1 ? '' : 's'} to this job`);
      onResumesSelected?.(count);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to add candidates');
    } finally {
      setAttaching(false);
    }
  };

  const count = picked.size;
  const goodFits = items.filter(c => c.jdMatchScore >= 75).length;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-slate-200">
          <div>
            <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-orange-500" /> Talent Pool — Good Fits
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Candidates from other roles scored against this job's JD.
              {goodFits > 0 && <span className="ml-1 font-semibold text-emerald-600">{goodFits} strong match{goodFits !== 1 ? 'es' : ''}.</span>}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Search + filter */}
        <div className="p-4 border-b border-slate-100 flex gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              autoFocus
              placeholder="Search by name, email, or applied JD…"
              className="w-full pl-10 pr-3 py-2 border border-slate-300 rounded-md focus:ring-orange-500 focus:border-orange-500 text-sm"
            />
          </div>
          <select
            value={minScore}
            onChange={e => setMinScore(Number(e.target.value))}
            className="border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-orange-500 focus:border-orange-500"
          >
            <option value={0}>All matches</option>
            <option value={40}>40%+ match</option>
            <option value={60}>60%+ match</option>
            <option value={75}>75%+ match</option>
          </select>
          <div className="flex items-center gap-3 text-xs text-slate-500 ml-auto">
            <span>{loading ? 'Loading…' : `${filtered.length} candidate${filtered.length !== 1 ? 's' : ''}`}</span>
            <button onClick={selectAll} className="text-orange-600 hover:underline disabled:opacity-40" disabled={!filtered.length}>Select all</button>
            <button onClick={clearAll} className="text-slate-500 hover:underline disabled:opacity-40" disabled={!count}>Clear</button>
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {loading && (
            <div className="flex items-center justify-center p-12 text-slate-500 gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Scoring candidates against this JD…
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <div className="p-10 text-center text-slate-500">
              <FileText className="w-10 h-10 mx-auto text-slate-300 mb-3" />
              <div className="text-sm font-medium">No matching candidates found</div>
              <div className="text-xs mt-1 text-slate-400">
                {items.length === 0
                  ? 'No candidates in your talent pool yet, or all are already on this job.'
                  : 'Try lowering the minimum match score filter.'}
              </div>
            </div>
          )}

          {!loading && filtered.length > 0 && (
            <div className="divide-y divide-slate-100">
              {filtered.map((r) => {
                const id = String(r._id);
                const isPicked = picked.has(id);
                const potential = r.candidatePotential ? POTENTIAL_LABEL[r.candidatePotential] : null;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => togglePick(id)}
                    className={`w-full flex items-center gap-3 py-3.5 px-2 text-left rounded-md transition ${isPicked ? 'bg-orange-50' : 'hover:bg-slate-50'}`}
                  >
                    {/* Checkbox */}
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${isPicked ? 'bg-orange-500 border-orange-500' : 'border-slate-300'}`}>
                      {isPicked && <CheckCircle className="w-4 h-4 text-white" />}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-slate-900">{r.candidateName}</span>
                        {potential && (
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${potential.cls}`}>
                            <Star className="w-2.5 h-2.5 inline mr-0.5" />{potential.label}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-400 mt-0.5 truncate">{r.email}</div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        Applied for: <span className="font-medium text-slate-700">{r.appliedJD}</span>
                      </div>
                      {r.skills?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {r.skills.slice(0, 5).map((s, i) => (
                            <span key={i} className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{s}</span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Match score */}
                    <div className={`shrink-0 text-center px-3 py-1.5 rounded-xl font-extrabold text-lg ${MATCH_COLOR(r.jdMatchScore)}`}>
                      {r.jdMatchScore}%
                      <div className="text-[9px] font-normal opacity-70">JD match</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 p-4 flex items-center justify-between">
          <div className="text-sm text-slate-600">
            {count > 0 ? `${count} selected` : 'Select candidates to add to this job'}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-slate-600 hover:text-slate-900 text-sm">Cancel</button>
            <button
              onClick={handleAttach}
              disabled={!count || attaching}
              className="px-4 py-2 rounded-md bg-orange-500 text-white font-medium hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
            >
              {attaching && <Loader2 className="w-4 h-4 animate-spin" />}
              Add {count > 0 ? `(${count})` : ''} to this job
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FetchResumesModal;
